import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Loader2, ArrowLeft, Network } from "lucide-react";
import SaveButton from "../components/SaveButton";

/**
 * KnowledgeGraphWorld — a chapter-level constellation inside one world.
 * Nodes = chapters, edges = module-adjacency (solid) or shared-keyword
 * (dashed).  Same pure-JS force simulation as the top-level graph — no
 * dependency.
 */
export default function KnowledgeGraphWorld() {
  const { world: worldSlug } = useParams();
  const [data, setData] = useState(null);
  const [positioned, setPositioned] = useState(null);
  const [hover, setHover] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!worldSlug) return;
    let cancelled = false;
    setData(null);
    setPositioned(null);
    setSelected(null);
    api.get(`/curriculum/graph/${worldSlug}?limit=60`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData({ notfound: true }); });
    return () => { cancelled = true; };
  }, [worldSlug]);

  useEffect(() => {
    if (!data || data.notfound || !data.nodes?.length) return;
    const W = 1400, H = 900, cx = W / 2, cy = H / 2;
    const nodes = data.nodes.map((n, i) => {
      const angle = (i / data.nodes.length) * Math.PI * 2;
      return { ...n, x: cx + Math.cos(angle) * 260 + (Math.random() - 0.5) * 40, y: cy + Math.sin(angle) * 260 + (Math.random() - 0.5) * 40, vx: 0, vy: 0 };
    });
    const byId = new Map(nodes.map((n) => [n.id, n]));

    const TICKS = 260;
    const REPULSION = 1400;
    const SPRING_K = 0.02;
    const LEN_MODULE = 80;
    const LEN_SHARED = 200;
    const GRAVITY = 0.0022;
    const DAMPING = 0.7;

    for (let t = 0; t < TICKS; t++) {
      for (const n of nodes) { n.fx = 0; n.fy = 0; }
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j];
          let dx = a.x - b.x, dy = a.y - b.y;
          let d2 = dx * dx + dy * dy;
          if (d2 < 1) { d2 = 1; dx = Math.random(); dy = Math.random(); }
          const f = REPULSION / d2;
          const d = Math.sqrt(d2);
          const fx = (dx / d) * f, fy = (dy / d) * f;
          a.fx += fx; a.fy += fy;
          b.fx -= fx; b.fy -= fy;
        }
      }
      for (const e of data.edges) {
        const a = byId.get(e.source), b = byId.get(e.target);
        if (!a || !b) continue;
        const dx = b.x - a.x, dy = b.y - a.y;
        const d = Math.sqrt(dx * dx + dy * dy) || 0.01;
        const targetLen = e.kind === "module" ? LEN_MODULE : LEN_SHARED;
        const strength = e.kind === "module" ? SPRING_K * 1.6 : SPRING_K * 0.6;
        const f = (d - targetLen) * strength;
        const fx = (dx / d) * f, fy = (dy / d) * f;
        a.fx += fx; a.fy += fy;
        b.fx -= fx; b.fy -= fy;
      }
      for (const n of nodes) {
        n.fx += (cx - n.x) * GRAVITY;
        n.fy += (cy - n.y) * GRAVITY;
      }
      for (const n of nodes) {
        n.vx = (n.vx + n.fx) * DAMPING;
        n.vy = (n.vy + n.fy) * DAMPING;
        n.x += n.vx;
        n.y += n.vy;
      }
    }
    setPositioned({ nodes, edges: data.edges, byId });
  }, [data]);

  // Colour per module
  const moduleColour = useMemo(() => {
    const palette = ["#F4A261", "#E76F51", "#2A9D8F", "#1F1A17", "#C9A24A"];
    if (!positioned) return () => "#F4A261";
    const modules = [...new Set(positioned.nodes.map((n) => n.module_code || "?"))].sort();
    const idx = new Map(modules.map((m, i) => [m, i]));
    return (m) => palette[(idx.get(m) ?? 0) % palette.length];
  }, [positioned]);

  if (!data) return <div className="container-narrow py-24 text-center text-ink2"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>;
  if (data.notfound) {
    return (
      <div className="container-narrow py-24 text-center" data-testid="graph-world-404">
        <h1 className="font-heading text-3xl font-semibold text-ink mb-4">This world hasn't been mapped yet.</h1>
        <Link to="/learn/graph" className="btn-outline-ink text-sm inline-flex items-center gap-1.5"><ArrowLeft className="w-4 h-4" /> Constellation</Link>
      </div>
    );
  }
  if (!positioned) return <div className="container-narrow py-24 text-center text-ink2"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>;

  const { nodes, edges, byId } = positioned;

  return (
    <div className="container-narrow py-10 md:py-14" data-testid={`graph-world-${worldSlug}`}>
      <Link to="/learn/graph" className="inline-flex items-center gap-1.5 text-sm text-ink2 hover:text-ink mb-6">
        <ArrowLeft className="w-4 h-4" /> The Constellation (all twelve)
      </Link>

      <div className="mb-6 flex items-start gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-2xl bg-marigold/25 text-marigold flex items-center justify-center flex-shrink-0">
          <Network className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-[260px]">
          <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-marigold mb-1">{data.world.kicker} · Constellation</div>
          <h1 className="font-heading text-4xl md:text-5xl font-semibold text-ink leading-[1.05] mb-2">
            {data.world.title}
          </h1>
          <p className="text-ink2 max-w-2xl">
            {data.chapter_count} chapters, {data.module_count} modules, threaded by module (solid) and shared keywords (dashed).
            <span className="hidden md:inline"> Hover a chapter to preview it; tap to open the reader.</span>
          </p>
        </div>
      </div>

      <div className="rounded-3xl border-2 border-ink bg-cream shadow-[8px_8px_0_0_#1F1A17] overflow-hidden">
        <svg viewBox="0 0 1400 900" className="w-full h-[65vh] min-h-[560px] bg-cream" data-testid="graph-world-svg" role="img" aria-label={`Constellation for ${data.world.title}`}>
          <g>
            {edges.map((e, i) => {
              const a = byId.get(e.source), b = byId.get(e.target);
              if (!a || !b) return null;
              const stroke = e.kind === "module" ? "#E76F51" : "#2A9D8F";
              const opacity = e.kind === "module" ? 0.55 : 0.25;
              const dash = e.kind === "shared" ? "5 5" : undefined;
              return <line key={`${e.kind}-${e.source}-${e.target}-${i}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={stroke} strokeWidth={e.kind === "module" ? 1.6 : 1} strokeDasharray={dash} opacity={opacity} />;
            })}
          </g>
          <g>
            {nodes.map((n) => {
              const fill = moduleColour(n.module_code);
              const r = selected?.id === n.id ? 12 : 7;
              return (
                <g
                  key={n.id}
                  transform={`translate(${n.x} ${n.y})`}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover((h) => (h && h.id === n.id ? null : h))}
                  onClick={() => setSelected(n)}
                  style={{ cursor: "pointer" }}
                >
                  <circle r={r + 3} fill="#1F1A17" opacity={0.12} />
                  <circle r={r} fill={fill} stroke="#1F1A17" strokeWidth={1.5} data-testid={`graph-world-node-${n.slug}`} />
                </g>
              );
            })}
          </g>
          {hover && (
            <g transform={`translate(${Math.min(hover.x + 12, 1150)} ${Math.max(hover.y - 34, 20)})`}>
              <rect x={0} y={0} rx={8} ry={8} width={Math.min(300, hover.label.length * 7 + 20)} height={26} fill="#1F1A17" />
              <text x={10} y={18} style={{ fill: "#FDF6E7", fontSize: 12, fontFamily: "Outfit, sans-serif" }}>
                {hover.label.length > 40 ? hover.label.slice(0, 38) + "…" : hover.label}
              </text>
            </g>
          )}
        </svg>

        {/* Detail panel — appears when a node is clicked */}
        {selected && (
          <div className="border-t-2 border-ink px-6 py-5 bg-cream flex items-start gap-4 flex-wrap" data-testid="graph-world-detail">
            <div className="flex-1 min-w-[240px]">
              <div className="text-[10px] font-mono font-bold text-ink/50 uppercase tracking-[0.2em] mb-1">{selected.code}</div>
              <div className="font-heading text-xl font-semibold text-ink leading-tight">{selected.label}</div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <SaveButton slug={selected.slug} title={selected.label} size="sm" testId={`graph-world-save-${selected.slug}`} />
              <Link to={`/library/${selected.slug}`} className="btn-primary text-xs inline-flex items-center gap-1.5" data-testid={`graph-world-open-${selected.slug}`}>
                Open chapter
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
